import React, { useEffect, useRef, useState } from 'react';
import { useDisclosure, useIdle } from '@mantine/hooks';
import * as styles from './UserEngagement.module.scss';
import { MdClose, MdKeyboardArrowDown } from 'react-icons/md';
import { MantineProvider, MultiSelect, Textarea, TextInput } from '@mantine/core';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { emailValidation } from '../../lib/email-validation';
import ButtonComponent from '../Home/ButtonComponent/ButtonComponent';
import { ndeFormInstance } from '../../lib/axios-client';
import posthog from 'posthog-js';
import { navigate } from 'gatsby';
import { toast } from 'react-toastify';

const schema = yup.object({
  name: yup
    .string()
    .required('Name is required.')
    .transform(value => value.trim())
    .min(3, 'Name must contain at least 3 letters.'),
  email: emailValidation,
  phone: yup
    .string()
    .required('Phone number is required.')
    .matches(/^[+]?[0-9\s\-()]{10,15}$/, 'Please enter a valid mobile number.'),
  service_items: yup.array().of(yup.string()).optional(),
  message: yup
    .string()
    .required('Please provide your message.')
    .min(10, 'Message should at least be 10 characters long.')
    .max(300, 'Message cannot exceed 300 characters.'),
});

export default function UserEngagement() {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [opened, { open, close }] = useDisclosure();
  const [apiLoading, setApiLoading] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const mouseLeaveTimeout = useRef<number | null>(null);

  const idle = useIdle(15000, { initialState: false });

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const triggerModal = () => {
    if (!hasMounted) return;

    const hasShown = sessionStorage.getItem('contact_form');
    const isDisabled = sessionStorage.getItem('userEngagementFormDisabled');
    const formModalOpen = sessionStorage.getItem('formOpen');

    if (!hasShown && !isDisabled && !formModalOpen) {
      open();
      sessionStorage.setItem('contact_form', 'true');
    }
  };

  const onSubmit = async (data: {
    email: string;
    name: string;
    phone: string;
    message: string;
    service_items?: (string | undefined)[] | undefined;
  }) => {
    setApiLoading(true);
    try {
      const landing_page = sessionStorage.getItem('landing_page');
      await ndeFormInstance.post('/lead-tracking', {
        ...data,
        service_items: data.service_items?.filter(
          (item): item is string => typeof item === 'string'
        ),
        form_id: 'close-intent-popup',
        landing_page,
      });
      if ((window as any).gtag) {
        (window as any).gtag('event', 'contact_form_submitted');
      }
      posthog.capture('form_submitted', {
        form_id: 'contact_form_submitted',
        submission_status: 'success',
      });
      close();
      navigate('/thank-you');
    } catch (error: any) {
      toast.error(error.message);
      console.error(error);
    } finally {
      setApiLoading(false);
    }
  };

  useEffect(() => {
    if (!hasMounted) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        const hasShown = sessionStorage.getItem('contact_form');
        if (!hasShown) {
          triggerModal();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasMounted, opened]);

  useEffect(() => {
    if (!hasMounted) return;

    const handleMouseLeave = (e: MouseEvent) => {
      if (
        e.clientY <= 0 ||
        e.clientX <= 0 ||
        e.clientX >= window.innerWidth ||
        e.clientY >= window.innerHeight
      ) {
        triggerModal();
      }
    };

    window.addEventListener('mouseout', handleMouseLeave);
    return () => {
      window.removeEventListener('mouseout', handleMouseLeave);
      if (mouseLeaveTimeout.current) clearTimeout(mouseLeaveTimeout.current);
    };
  }, [hasMounted]);

  useEffect(() => {
    if (!hasMounted) return;

    const hasShown = sessionStorage.getItem('contact_form');
    if (hasShown) return;

    if (idle) {
      triggerModal();
    }
  }, [idle, hasMounted]);

  return (
    <MantineProvider>
      {opened && (
        <div className={styles.UserEngagement__backdrop}>
          <div className={styles.UserEngagement__container}>
            <button className={styles.UserEngagement__closeButton} onClick={close}>
              Close <MdClose size={20} />
            </button>
            <div className={styles.UserEngagement__modal}>
              <div className={styles.UserEngagement__heading}>
                Your next big idea deserves better design, talk to us!!
              </div>

              <form
                onSubmit={handleSubmit(onSubmit)}
                className={styles.UserEngagement__formContainer}
              >
                <TextInput
                  placeholder='What is your name? *'
                  size='lg'
                  className={styles.UserEngagement__fieldArea}
                  {...register('name')}
                  error={errors.name?.message}
                />

                <div className={styles.UserEngagement__rowContainer}>
                  <TextInput
                    placeholder='Your email for contact *'
                    size='lg'
                    className={styles.UserEngagement__halfFieldArea}
                    {...register('email')}
                    error={errors.email?.message}
                  />

                  <TextInput
                    placeholder='Mobile number *'
                    size='lg'
                    className={styles.UserEngagement__halfFieldArea}
                    {...register('phone')}
                    error={errors.phone?.message}
                  />
                </div>

                <Controller
                  control={control}
                  name='service_items'
                  render={({ field }) => (
                    <MultiSelect
                      data={[
                        'Website Design',
                        'Mobile App Design',
                        'SaaS Product Design',
                        'eCommerce',
                        'UI/UX Audit',
                        'Other',
                      ]}
                      placeholder='Project Type'
                      size='lg'
                      className={styles.UserEngagement__serviceItems}
                      classNames={{
                        input: styles.inputField,
                        option: styles.multiValue,
                        pill: styles.pills,
                        section: styles.UserEngagement__margin,
                      }}
                      rightSection={<MdKeyboardArrowDown size={20} />}
                      onChange={field.onChange}
                      error={errors.service_items?.message}
                    />
                  )}
                />

                <div className={styles.UserEngagement__textareaContainer}>
                  <Textarea
                    classNames={{
                      input: styles.messageField,
                    }}
                    placeholder='Message *'
                    error={errors.message?.message}
                    {...register('message', {
                      onChange: e => setMessageLength(e.target.value.length),
                    })}
                  />
                  <div className={styles.UserEngagement__characterCount}>
                    {messageLength}/300 characters
                  </div>
                </div>

                <ButtonComponent
                  buttonText={'Submit'}
                  buttonStyle={'solidBg'}
                  type='submit'
                  className={styles.UserEngagement__submit_button}
                  onClick={undefined}
                  disabled={undefined}
                  loading={apiLoading}
                />
              </form>
            </div>
          </div>
        </div>
      )}
    </MantineProvider>
  );
}
